import classNames from 'classnames';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { ArrowDownGrayIcon, ArrowUpGrayIcon, LockDisabledIcon } from '../../icons';
import { Tooltip } from '../Tooltip';
import { TooltipNew } from '../TooltipNew';
import { SidebarMenuItem } from './types';

interface MenuItemProps {
  paddingY: string;
  isOpened: boolean;
  setIsOpened?: Dispatch<SetStateAction<boolean>>;
  isOpenItem: boolean;
  toggleOpenItem: () => void;
  isSearchUsed?: boolean;
  search?: string;
  item: SidebarMenuItem;
}

export const MenuItem = ({
  item,
  search,
  paddingY,
  isOpened,
  isOpenItem,
  toggleOpenItem,
  isSearchUsed,
  setIsOpened,
}: MenuItemProps) => {
  const {
    disabled,
    subItems,
    labelClassName,
    label,
    disabledHint,
    disabledIcon,
    icon,
    labelAction,
    activeIcon,
    notification,
    pathname,
    opener,
    onClick,
    tooltipHelpText,
  } = item;

  const resolveLabel = useMemo(() => {
    if (!search || typeof label !== 'string') return label;

    const regExp = new RegExp(search, 'gi');
    const matches = [...label.matchAll(regExp)];

    const items: [number, number, boolean][] = matches.reduce((acc, curr) => {
      if (typeof curr.index !== 'number') return acc;

      const prev = acc.length && acc.at(-1);

      if (!prev && curr.index) acc.push([0, curr.index, false]);
      if (prev && prev[1] < curr.index) acc.push([prev[1], curr.index, false]);

      return [...acc, [curr.index, curr.index + curr[0].length, true]];
    }, [] as [number, number, boolean][]);

    const lastItem = items.length && items.at(-1);

    if (lastItem && lastItem[1] < label.length) {
      items.push([lastItem[1], label.length, false]);
    }

    if (!items.length) return label;
    return (
      <span>
        {items.map(([start, end, isMatch], i) => (
          <span key={i} className={classNames({ 'text-white': isMatch })}>
            {label.substring(start, end)}
          </span>
        ))}
      </span>
    );
  }, [label, search]);

  return (
    <TooltipNew
      text={tooltipHelpText || ''}
      html={tooltipHelpText || ''}
      className="w-full"
      place="right"
      positionStrategy="fixed"
      offset={25}
      showDelay={800}
      hideDelay={800}
      disabled={!tooltipHelpText}
    >
      <div
        onClick={
          disabled
            ? undefined
            : e => {
                e.stopPropagation();

                opener && subItems && subItems.length && toggleOpenItem();
                !opener && setIsOpened && setIsOpened(false);
                onClick && onClick();
              }
        }
        className={classNames('w-full flex items-center px-4', {
          'cursor-pointer hover:bg-white hover:bg-opacity-[5%] border-l-2 border-l-transparent':
            !disabled && window.location.pathname !== pathname,
          'bg-white bg-opacity-[7%] border-l-2 border-l-[#0EA5E9]':
            window.location.pathname === pathname && !disabled,
        })}
        style={{
          paddingTop: paddingY,
          paddingBottom: paddingY,
        }}
      >
        {icon && (
          <img
            className="mr-3 select-none min-w-6 min-h-6 w-6 h-6"
            src={
              disabled ? disabledIcon : window.location.pathname === pathname ? activeIcon : icon
            }
            alt="Loading..."
          />
        )}
        {isOpened && (
          <>
            <span
              className={classNames(
                'text-[20px] sm:text-[14px] leading-[22px]',
                {
                  'text-gray-400': window.location.pathname !== pathname && !disabled,
                  'text-gray-600': disabled,
                },
                labelClassName,
              )}
            >
              <div className="truncate">{resolveLabel}</div>
              {labelAction}
            </span>
            {subItems && !!subItems.length ? (
              <span className="ml-auto flex items-center gap-2">
                {isSearchUsed && <span className="text-sm opacity-64">{subItems.length}</span>}
                <span
                  className="select-none cursor-pointer hover:bg-gray-700 p-[2px] rounded-full"
                  onClick={
                    subItems
                      ? e => {
                          e.stopPropagation();
                          toggleOpenItem();
                        }
                      : undefined
                  }
                >
                  <img
                    className="sm:w-5 sm:h-5 sm:min-w-5 sm:min-h-5 w-7 h-7 min-w-7 min-h-7"
                    src={isOpenItem ? ArrowUpGrayIcon : ArrowDownGrayIcon}
                    alt="Loading..."
                  />
                </span>
              </span>
            ) : (
              <>
                {disabled && (
                  <Tooltip
                    className="ml-auto select-none"
                    classNameTooltip="mt-[-80px]"
                    position={null}
                    delayTimeOpen={0}
                    delayTimeClose={100}
                    positionHorizontal="left"
                    button={
                      <img
                        className="ml-auto select-none w-5 h-5 min-w-[20px] min-h-[20px]"
                        src={LockDisabledIcon}
                        alt="Loading..."
                      />
                    }
                    disabled={!disabledHint}
                    variant="black"
                  >
                    {disabledHint}
                  </Tooltip>
                )}
                {notification && (
                  <div className="ml-auto select-none mt-[2px] w-2 h-2 rounded-full bg-red-600"></div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </TooltipNew>
  );
};
